import { hydrateSettingsFromServer } from 'api/resource/user';
import { useIsAdmin } from 'hooks';
import { useEffect, useState } from 'react';
import {
  getSettingsPayloadSignature,
  syncSettingsOnClose,
  syncSettingsWithBackend,
  useSettingsStore,
} from 'store/SettingsStore';
import { useCurrentUserStore } from 'store/UserStore';
import { closeWebSocket } from 'api/ws/session';

export const SettingsSyncManager = () => {
  const { id: userID } = useCurrentUserStore();
  const isAdmin = useIsAdmin();
  const settings = useSettingsStore();
  const settingsSignature = getSettingsPayloadSignature(settings);
  const [readyUserID, setReadyUserID] = useState<string | null>(null);

  useEffect(() => {
    if (!userID) return;
    let cancelled = false;
    setReadyUserID(null);
    void (async () => {
      if (!isAdmin) {
        if (useSettingsStore.getState().dirty) {
          const saved = await syncSettingsWithBackend();
          if (cancelled) return;
          if (!saved) {
            setReadyUserID(userID);
            return;
          }
        }
        await hydrateSettingsFromServer(userID);
      }
      if (cancelled) return;
      setReadyUserID(userID);
    })();
    return () => {
      cancelled = true;
    };
  }, [userID, isAdmin]);

  useEffect(() => {
    if (isAdmin || readyUserID !== userID || !settings.dirty) return;
    const timeoutID = window.setTimeout(() => {
      void syncSettingsWithBackend();
    }, 500);
    return () => window.clearTimeout(timeoutID);
  }, [isAdmin, readyUserID, userID, settings.dirty, settingsSignature]);

  return null;
};
export const SettingsUnloadSync = () => {
  const dirty = useSettingsStore((s) => s.dirty);

  // open a dialog when there are unsaved changes and the user tries to close the tab
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      closeWebSocket();
      if (!dirty) return;
      syncSettingsOnClose();
      e.preventDefault();
      e.returnValue = ''; // Chrome requires
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dirty]);

  return null;
};
